import React, { useEffect, useState } from "react";
import {
  motion,
  useMotionValue,
  useTransform,
  animate,
} from "framer-motion";
import { useOutletContext, Link } from "react-router-dom";

import {
  UserPlus,
  GraduationCap,
  FileText,
  Award,
  Download,
  Link2,
  ShieldCheck,
} from "lucide-react";

import adminApi from "./adminApi";


/* =========================================================
   QUICK ACTIONS
========================================================= */

const QUICK_ACTIONS = [
  {
    label: "Student Registrations",
    path: "student-registration",
    icon: UserPlus,
    bg: "#f0fdfa",
    border: "#99f6e4",
    color: "#0f766e",
    iconBg: "#ccfbf1",
    hover: "#e6fffa",
  },
  {
    label: "Manage Tenders",
    path: "tenders",
    icon: FileText,
    bg: "#eff6ff",
    border: "#bfdbfe",
    color: "#1d4ed8",
    iconBg: "#dbeafe",
    hover: "#e5efff",
  },
  {
    label: "Manage Results",
    path: "results",
    icon: Award,
    bg: "#fffbeb",
    border: "#fde68a",
    color: "#b45309",
    iconBg: "#fef3c7",
    hover: "#fff8dc",
  },
  {
    label: "Download Forms",
    path: "download-forms",
    icon: Download,
    bg: "#fff1f2",
    border: "#fecdd3",
    color: "#be123c",
    iconBg: "#ffe4e6",
    hover: "#ffe9ec",
  },
  {
    label: "Website Links",
    path: "links",
    icon: Link2,
    bg: "#f0f9ff",
    border: "#bae6fd",
    color: "#0369a1",
    iconBg: "#e0f2fe",
    hover: "#e8f7ff",
  },
  {
    label: "Govt. Approval",
    path: "govt-approval",
    icon: ShieldCheck,
    bg: "#faf5ff",
    border: "#e9d5ff",
    color: "#7e22ce",
    iconBg: "#f3e8ff",
    hover: "#f3e8ff",
  },
];


/* =========================================================
   ANIMATED COUNTER
========================================================= */

function Counter({ to, duration = 1.1 }) {
  const mv = useMotionValue(0);

  const rounded = useTransform(
    mv,
    (v) => Math.round(v).toLocaleString()
  );

  useEffect(() => {
    const controls = animate(mv, to, {
      duration,
      ease: "easeOut",
    });

    return controls.stop;
  }, [to, duration, mv]);

  return <motion.span>{rounded}</motion.span>;
}


/* =========================================================
   MOTION PRESETS
========================================================= */

const container = {
  hidden: {},

  show: {
    transition: {
      staggerChildren: 0.07,
    },
  },
};

const item = {
  hidden: {
    opacity: 0,
    y: 14,
  },

  show: {
    opacity: 1,
    y: 0,

    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};


/* =========================================================
   ADMIN DASHBOARD HOME
========================================================= */

export default function AdminDashboardHome() {

  const { user } = useOutletContext();


  /* =========================================================
     STATE
  ========================================================= */

  const [stats, setStats] = useState({
    registrations: null,
    admissions: null,
    tenders: null,
    results: null,
  });

  const [recentStudents, setRecentStudents] = useState([]);

  const [loadingRecent, setLoadingRecent] = useState(true);


  /* =========================================================
     FETCH DATA
  ========================================================= */

  useEffect(() => {

    let cancelled = false;

    (async () => {

      const [
        regRes,
        admRes,
        tenRes,
        resRes,
      ] = await Promise.allSettled([

        adminApi.get(
          "/admin/student-registrations"
        ),

        adminApi.get(
          "/admin/admission"
        ),

        adminApi.get(
          "/admin/tenders"
        ),

        adminApi.get(
          "/admin/results"
        ),

      ]);


      if (cancelled) {
        return;
      }


      /* -----------------------------------------------------
         STUDENTS
      ----------------------------------------------------- */

      const students =
        regRes.status === "fulfilled"
          ? (
              regRes.value.data.students ||
              []
            )
          : [];


      /* -----------------------------------------------------
         STATISTICS
      ----------------------------------------------------- */

      setStats({

        registrations:
          regRes.status === "fulfilled"
            ? students.length
            : null,

        admissions:
          admRes.status === "fulfilled"
            ? (
                admRes.value.data.admissions ||
                []
              ).length
            : null,

        tenders:
          tenRes.status === "fulfilled"
            ? (
                tenRes.value.data.tenders ||
                []
              ).length
            : null,

        results:
          resRes.status === "fulfilled"
            ? (
                resRes.value.data.results ||
                []
              ).length
            : null,

      });


      /* -----------------------------------------------------
         LATEST 10 STUDENTS
      ----------------------------------------------------- */

      setRecentStudents(
        students.slice(0, 10)
      );

      setLoadingRecent(false);

    })();


    return () => {
      cancelled = true;
    };

  }, []);


  /* =========================================================
     STATISTICS
  ========================================================= */

  const STATS = [

    {
      label: "Student Registrations",
      value: stats.registrations,
      icon: UserPlus,

      color: "#0f766e",
      bg: "#f0fdfa",
      iconBg: "#ccfbf1",
      accent: "#14b8a6",
    },

    {
      label: "Admission Listings",
      value: stats.admissions,
      icon: GraduationCap,

      color: "#1d4ed8",
      bg: "#eff6ff",
      iconBg: "#dbeafe",
      accent: "#3b82f6",
    },

    {
      label: "Tenders",
      value: stats.tenders,
      icon: FileText,

      color: "#b45309",
      bg: "#fffbeb",
      iconBg: "#fef3c7",
      accent: "#f59e0b",
    },

    {
      label: "Results",
      value: stats.results,
      icon: Award,

      color: "#be123c",
      bg: "#fff1f2",
      iconBg: "#ffe4e6",
      accent: "#f43f5e",
    },

  ];


  /* =========================================================
     PAGE
  ========================================================= */

  return (

    <>
      {/* =====================================================
          TABLE CSS
      ===================================================== */}

      <style>
        {`

          .dashboard-table {
            width: 100%;
            border-collapse: collapse;
            border: 1px solid #cbd5e1;
          }

          .dashboard-table thead tr {
            background-color: #dbeafe !important;
          }

          .dashboard-table thead th {
            color: #0f172a !important;
            background-color: #dbeafe !important;
            font-weight: 700 !important;
            text-align: left !important;

            border-right:
              1px solid #93c5fd !important;

            border-bottom:
              2px solid #60a5fa !important;

            letter-spacing: 0.5px;
            white-space: nowrap;
          }

          .dashboard-table thead th:last-child {
            border-right: none !important;
          }

          .dashboard-table tbody td {
            border-right:
              1px solid #cbd5e1;

            border-bottom:
              1px solid #cbd5e1;
          }

          .dashboard-table tbody td:last-child {
            border-right: none;
          }

          .dashboard-table tbody tr:hover {
            background-color: #eff6ff !important;
          }

        `}
      </style>


      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="space-y-6"
      >


        {/* ===================================================
            STATISTICS CARDS
        =================================================== */}

        <motion.div
          variants={container}

          className="
            grid
            grid-cols-1
            sm:grid-cols-2
            lg:grid-cols-4
            gap-4
          "
        >

          {STATS.map((s) => {

            const Icon = s.icon;


            return (

              <motion.div
                key={s.label}

                variants={item}

                whileHover={{
                  y: -3,
                }}

                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 22,
                }}

                className="
                  relative
                  overflow-hidden
                  rounded-xl
                  border
                  shadow-sm
                  hover:shadow-md
                  transition-shadow
                  duration-200
                "

                style={{
                  backgroundColor: s.bg,
                  borderColor: "#e2e8f0",
                }}
              >

                {/* TOP COLOR LINE */}

                <div
                  className="
                    absolute
                    top-0
                    left-0
                    right-0
                    h-[3px]
                  "

                  style={{
                    backgroundColor:
                      s.accent,
                  }}
                />


                <div className="p-4">


                  {/* =================================================
                      ICON + NUMBER SAME LINE
                  ================================================= */}

                  <div
                    className="
                      flex
                      items-center
                      gap-3
                    "
                  >

                    {/* ICON */}

                    <div
                      className="
                        flex
                        items-center
                        justify-center
                        w-9
                        h-9
                        rounded-lg
                        flex-shrink-0
                      "

                      style={{
                        backgroundColor:
                          s.iconBg,

                        color:
                          s.color,
                      }}
                    >

                      <Icon
                        size={18}
                        strokeWidth={2}
                      />

                    </div>


                    {/* NUMBER */}

                    <p
                      className="
                        text-[27px]
                        font-bold
                        leading-none
                        m-0
                      "

                      style={{
                        color:
                          "#0b1220",
                      }}
                    >

                      {s.value === null
                        ? "—"
                        : (
                          <Counter
                            to={s.value}
                          />
                        )}

                    </p>

                  </div>


                  {/* LABEL */}

                  <p
                    className="
                      text-[14px]
                      font-medium
                      mt-2
                    "

                    style={{
                      color:
                        s.color,
                    }}
                  >
                    {s.label}
                  </p>

                </div>

              </motion.div>

            );

          })}

        </motion.div>


        {/* ===================================================
            QUICK ACTIONS
        =================================================== */}

        <motion.div
          variants={item}
          className="card"

          style={{
            padding: "10px",
          }}
        >

          <div className="mb-3">

            <h3
              className="
                font-display
                font-semibold
                text-[15px]
              "

              style={{
                color:
                  "#0b1f3a",
              }}
            >
              Quick Links
            </h3>


            {/* <p
              className="
                text-[11px]
                text-slate-400
                mt-0.5
              "
            >
              Jump straight to a section
            </p> */}

          </div>


          {/* QUICK ACTION BOXES */}

          <div
            className="
              grid
              grid-cols-2
              sm:grid-cols-3
              lg:grid-cols-6
              gap-2
            "
          >

            {QUICK_ACTIONS.map((q) => {

              const Icon = q.icon;


              return (

                <Link
                  key={q.label}

                  to={`/admin/${q.path}`}

                  className="
                    group
                    flex
                    items-center
                    gap-2
                    rounded-lg
                    border
                    px-2
                    py-2
                    transition-all
                    duration-200
                    hover:-translate-y-0.5
                    hover:shadow-sm
                  "

                  style={{
                    backgroundColor:
                      q.bg,

                    borderColor:
                      q.border,
                  }}

                  onMouseEnter={(e) => {

                    e.currentTarget.style
                      .backgroundColor =
                      q.hover;

                  }}

                  onMouseLeave={(e) => {

                    e.currentTarget.style
                      .backgroundColor =
                      q.bg;

                  }}
                >

                  {/* ICON */}

                  <span
                    className="
                      flex-shrink-0
                      flex
                      items-center
                      justify-center
                      w-8
                      h-8
                      rounded-lg
                    "

                    style={{
                      backgroundColor:
                        q.iconBg,

                      color:
                        q.color,
                    }}
                  >

                    <Icon
                      size={16}
                      strokeWidth={2}
                    />

                  </span>


                  {/* LABEL */}

                  <span
                    className="
                      text-[11px]
                      font-semibold
                      leading-tight
                    "

                    style={{
                      color:
                        q.color,
                    }}
                  >
                    {q.label}
                  </span>

                </Link>

              );

            })}

          </div>

        </motion.div>


        {/* ===================================================
            RECENT STUDENT REGISTRATIONS
        =================================================== */}

        <motion.div
          variants={item}

          className="
            card
            overflow-hidden
          "
        >


          {/* =================================================
              SECTION HEADER
          ================================================= */}

          <div
            className="
              px-1
              py-1
              flex
              items-center
              justify-between
              border-b
            "

            style={{
              backgroundColor:
                "#f8fafc",

              borderColor:
                "#cbd5e1",
            }}
          >

            <div>

              <h3
                className="
                  font-display
                  font-semibold
                  text-[16px]
                "

                style={{
                  color:
                    "#123b63",
                }}
              >
                Recent 10 Student Registrations
              </h3>


              {/* <p
                className="
                  text-[11px]
                  mt-1
                "

                style={{
                  color:
                    "#64748b",
                }}
              >
                Latest 10 student registration records
              </p> */}

            </div>


            <Link
              to="/admin/student-registration"

              className="
                text-[14px]
                font-semibold
                px-3
                py-1.5
                rounded-md
              "

              style={{
                color:
                  "#1d4ed8",

                backgroundColor:
                  "#eff6ff",
              }}
            >
              View All →
            </Link>

          </div>


          {/* =================================================
              TABLE
          ================================================= */}

          <div className="overflow-x-auto">

            <table
              className="
                dashboard-table
                text-sm
              "
            >


              {/* TABLE HEADING */}

              <thead>

                <tr>

                  <th
                    className="
                      px-5
                      py-3
                      text-[11px]
                      uppercase
                    "
                  >
                    Reg. ID
                  </th>


                  <th
                    className="
                      px-5
                      py-3
                      text-[11px]
                      uppercase
                    "
                  >
                    Student Name
                  </th>


                  <th
                    className="
                      px-5
                      py-3
                      text-[11px]
                      uppercase
                    "
                  >
                    Course
                  </th>


                  <th
                    className="
                      px-5
                      py-3
                      text-[11px]
                      uppercase
                    "
                  >
                    Status
                  </th>


                  <th
                    className="
                      px-5
                      py-3
                      text-[11px]
                      uppercase
                    "
                  >
                    Registration Date
                  </th>

                </tr>

              </thead>


              {/* =================================================
                  TABLE BODY
              ================================================= */}

              <tbody>


                {/* LOADING */}

                {loadingRecent && (

                  <tr>

                    <td
                      colSpan={5}
                      className="
                        px-5
                        py-8
                        text-center
                      "
                    >

                      <span
                        className="
                          text-[13px]
                        "

                        style={{
                          color:
                            "#64748b",
                        }}
                      >
                        Loading student registrations...
                      </span>

                    </td>

                  </tr>

                )}


                {/* NO DATA */}

                {!loadingRecent &&
                  recentStudents.length === 0 && (

                    <tr>

                      <td
                        colSpan={5}
                        className="
                          px-5
                          py-8
                          text-center
                        "
                      >

                        <span
                          className="
                            text-[13px]
                            font-medium
                          "

                          style={{
                            color:
                              "#64748b",
                          }}
                        >
                          No student registrations found.
                        </span>

                      </td>

                    </tr>

                  )}


                {/* =================================================
                    STUDENT ROWS
                ================================================= */}

                {!loadingRecent &&
                  recentStudents
                    .slice(0, 10)
                    .map((r, i) => (

                      <motion.tr
                        key={r.reg_id}

                        initial={{
                          opacity: 0,
                          y: 5,
                        }}

                        animate={{
                          opacity: 1,
                          y: 0,
                        }}

                        transition={{
                          delay:
                            i * 0.04,

                          duration:
                            0.25,
                        }}

                        style={{
                          backgroundColor:
                            i % 2 === 0
                              ? "#ffffff"
                              : "#f8fafc",
                        }}
                      >


                        {/* REG ID */}

                        <td
                          className="
                            px-5
                            py-3
                          "
                        >

                          <span
                            className="
                              
                              text-[11px]
                              font-semibold
                              px-2
                              py-1
                              rounded
                            "

                            style={{
                              color:
                                "#1d4ed8",

                              backgroundColor:
                                "#eff6ff",
                            }}
                          >
                            MKCG-{r.reg_id}
                          </span>

                        </td>


                        {/* STUDENT NAME */}

                        <td
                          className="
                            px-5
                            py-3
                          "
                        >

                          <span
                            className="
                              text-[13px]
                              font-semibold
                            "

                            style={{
                              color:
                                "#1e293b",
                            }}
                          >
                            {r.st_name ||
                              "—"}
                          </span>

                        </td>


                        {/* COURSE */}

                        <td
                          className="
                            px-5
                            py-3
                          "
                        >

                          <span
                            className="
                              text-[14px]
                            "

                            style={{
                              color:
                                "#475569",
                            }}
                          >
                            {r.course_name ||
                              "—"}
                          </span>

                        </td>


                        {/* STATUS */}

                        <td
                          className="
                            px-5
                            py-3
                          "
                        >

                          <span
                            className="
                              inline-flex
                              items-center
                              gap-1.5
                              px-2.5
                              py-1
                              rounded-full
                              text-[11px]
                              font-semibold
                            "

                            style={{
                              color:
                                r.status
                                  ? "#047857"
                                  : "#b45309",

                              backgroundColor:
                                r.status
                                  ? "#ecfdf5"
                                  : "#fffbeb",
                            }}
                          >

                            <span
                              className="
                                w-1.5
                                h-1.5
                                rounded-full
                              "

                              style={{
                                backgroundColor:
                                  r.status
                                    ? "#10b981"
                                    : "#f59e0b",
                              }}
                            />

                            {r.status
                              ? "Active"
                              : "Pending"}

                          </span>

                        </td>


                        {/* REGISTRATION DATE */}

                        <td
                          className="
                            px-5
                            py-3
                          "
                        >

                          <span
                            className="
                              text-[14px]
                            "

                            style={{
                              color:
                                "#64748b",
                            }}
                          >

                            {r.created_date
                              ? new Date(
                                  r.created_date
                                ).toLocaleDateString(
                                  "en-IN",
                                  {
                                    day:
                                      "2-digit",

                                    month:
                                      "short",

                                    year:
                                      "numeric",
                                  }
                                )
                              : "—"}

                          </span>

                        </td>

                      </motion.tr>

                    ))}

              </tbody>

            </table>

          </div>


          {/* =================================================
              TABLE FOOTER
          ================================================= */}

          {!loadingRecent &&
            recentStudents.length > 0 && (

              <div
                className="
                  px-5
                  py-3
                  flex
                  items-center
                  justify-between
                  border-t
                "

                style={{
                  backgroundColor:
                    "#f8fafc",

                  borderColor:
                    "#cbd5e1",
                }}
              >

                <span
                  className="
                    text-[11px]
                  "

                  style={{
                    color:
                      "#64748b",
                  }}
                >
                  Showing{" "}
                  {Math.min(
                    recentStudents.length,
                    10
                  )}{" "}
                  recent registrations
                </span>


                {/* <Link
                  to="/admin/student-registration"

                  className="
                    text-[11px]
                    font-semibold
                  "

                  style={{
                    color:
                      "#1d4ed8",
                  }}
                >
                  Manage Registrations →
                </Link> */}

              </div>

            )}

        </motion.div>

      </motion.div>

    </>

  );
}